import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  CssBaseline,
  Select,
  FormControl
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import RoomIcon from '@mui/icons-material/Room';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useAlerts } from '../contexts/AlertsContext';
import Badge from '@mui/material/Badge';

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { label: 'Location', icon: <RoomIcon />, path: '/live-tracking' },
  { label: 'Playback', icon: <HistoryIcon />, path: '/playback' },
  { label: 'Event', icon: <EventIcon />, path: '/event' },
  { label: 'Logistics', icon: <LocalShippingIcon />, path: '/logistics' },
  { label: 'Event Status', icon: <AssessmentIcon />, path: '/event-status' },
  { label: 'Attendance', icon: <CheckCircleIcon />, path: '/attendance' },
  { label: 'Emergency', icon: <WarningIcon />, path: '/emergency-alerts' },
  { label: 'Tickets', icon: <ConfirmationNumberIcon />, path: '/ticket-tracking' },
];

const Layout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const { alertsCount } = useAlerts();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };
  const handleProfile = () => {
    handleProfileMenuClose();
    navigate('/profile');
  };
  const handleLogout = async () => {
    handleProfileMenuClose();
    // Use your existing logout logic here
    window.location.href = '/login';
  };

  const handleLanguageChange = (event: any) => {
    const newLang = event.target.value;
    setLanguage(newLang);
    localStorage.setItem('language', newLang);
    i18n.changeLanguage(newLang);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <AppBar position="sticky" sx={{ background: '#c62828', zIndex: 1201 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', minHeight: 56 }}>
          {/* Left: App Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/onemove-icon.png"
              alt="OneMove Logo"
              style={{ height: 36, width: 36, marginRight: 12, borderRadius: 8, background: '#fff' }}
            />
          </Box>
          {/* Center: Navigation Buttons */}
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            {navItems.map((item) => (
              <Tooltip title={item.label} key={item.label} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{ mx: 0.5, borderRadius: 2 }}
                >
                  {item.label === 'Emergency' ? (
                    <Badge
                      sx={{
                        '& .MuiBadge-badge': {
                          backgroundColor: '#ffeb3b', // Bright yellow
                          color: '#222',
                          fontWeight: 700,
                          boxShadow: '0 0 0 2px #c62828', // Outline for contrast
                          animation: alertsCount > 0 ? 'pulse 2s infinite' : 'none',
                          right: -10,
                          top: 6,
                        },
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' },
                          '100%': { transform: 'scale(1)' },
                        },
                      }}
                      badgeContent={alertsCount > 0 ? alertsCount : null}
                      overlap="circular"
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <WarningIcon />
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
          {/* Right: Language Dropdown and Profile Icon */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Language Dropdown */}
            <Tooltip title="Language" arrow>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  sx={{
                    color: 'white',
                    '& .MuiSelect-icon': { color: 'white' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    '& .MuiSelect-select': { py: 0.5, px: 1 }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#fff',
                        '& .MuiMenuItem-root': {
                          fontSize: '0.875rem'
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="en">🇺🇸 EN</MenuItem>
                  <MenuItem value="ms">🇲🇾 MS</MenuItem>
                  <MenuItem value="zh">🇨🇳 ZH</MenuItem>
                </Select>
              </FormControl>
            </Tooltip>
            
            {/* Profile Icon */}
            <Tooltip title="Profile" arrow>
              <IconButton color="inherit" onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
                <AccountCircleIcon fontSize="large" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={handleProfile}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
        {children}
      </Box>
    </Box>
  );
};

export default Layout; 